<script setup>
import { computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import V4Button from 'dashboard/components-next/button/Button.vue';
import ReportHeader from './components/ReportHeader.vue';
import ComplianceTable from './components/Compliance/ComplianceTable.vue';
import ComplianceMetrics from './components/Compliance/ComplianceMetrics.vue';

const store = useStore();
const { t } = useI18n();

const complianceReports = computed(
  () => store.getters['complianceReports/getAll']
);
const meta = computed(() => store.getters['complianceReports/getMeta']);
const uiFlags = computed(() => store.getters['complianceReports/getUIFlags']);

let refreshInterval = null;

const fetchReports = () => {
  store.dispatch('complianceReports/get');
};

const downloadReports = async () => {
  try {
    await store.dispatch('complianceReports/download');
    useAlert(t('COMPLIANCE.DOWNLOAD_SUCCESS'));
  } catch (error) {
    useAlert(t('COMPLIANCE.DOWNLOAD_FAILED'));
  }
};

const downloadHistory = async () => {
  try {
    await store.dispatch('complianceReports/downloadHistory');
    useAlert(t('COMPLIANCE.DOWNLOAD_SUCCESS'));
  } catch (error) {
    useAlert(t('COMPLIANCE.DOWNLOAD_FAILED'));
  }
};

onMounted(() => {
  fetchReports();
  refreshInterval = setInterval(fetchReports, 30000);
});

onUnmounted(() => {
  if (refreshInterval) {
    clearInterval(refreshInterval);
  }
});
</script>

<template>
  <ReportHeader :header-title="$t('COMPLIANCE.HEADER')">
    <div class="flex gap-2">
      <V4Button
        :label="$t('COMPLIANCE.REFRESH')"
        icon="i-ph-arrow-clockwise"
        variant="ghost"
        size="sm"
        :loading="uiFlags.isFetching"
        @click="fetchReports"
      />
      <V4Button
        :label="$t('COMPLIANCE.DOWNLOAD_REPORT')"
        icon="i-ph-download-simple"
        variant="ghost"
        size="sm"
        :loading="uiFlags.isDownloading"
        @click="downloadReports"
      />
      <V4Button
        :label="$t('COMPLIANCE.DOWNLOAD_HISTORY')"
        icon="i-ph-clock-counter-clockwise"
        size="sm"
        :loading="uiFlags.isDownloading"
        @click="downloadHistory"
      />
    </div>
  </ReportHeader>
  <div class="flex flex-col flex-1 gap-6">
    <ComplianceMetrics
      :total-count="meta.totalCount"
      :exceeding-count="meta.exceedingCount"
      :threshold-minutes="meta.thresholdMinutes"
      :is-loading="uiFlags.isFetching"
    />
    <ComplianceTable
      :reports="complianceReports"
      :is-loading="uiFlags.isFetching"
    />
  </div>
</template>
